import PropTypes from 'prop-types';
import { StatisticsLayout } from 'views/components';
import isEmpty from 'lodash/isEmpty';
import { parseKey } from './fixtures';
import { statisticsActions } from 'core/Statistics';
import { useTranslation } from 'react-i18next';
import {
    BarChart,
    Card,
    InfoCard,
    PieChart
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import React, { useCallback, useMemo, useState } from 'react';
import {
    clearObject,
    isLocked,
    objectToChart
} from 'shared/utils';
import {
    useDispatch,
    useSelector
} from 'hooks';

const HealthSurveys  = ({
    tabController
}) => {

    const { t } = useTranslation();
    const {
        loading,
        healthSurveys,
        healthSurveysDownload,
        healthSurveysDownloading
    } = useSelector('statistics');
    const { user } = useSelector('account');
    const dispatch = useDispatch();
    const [, setCurrentFilters] = useState({});
    
    const extractDownloadHeaders = list => {
        if (isEmpty(list)) return [];
        return list[0].answers.map(answer => ({
            key: answer.survey_question_uuid,
            label: answer.survey_question
        }));
    };

    const downloadHeaders = useMemo(() => [{
        key: 'created_at',
        label: t('Fecha')
    }, {
        key: 'patient_identification',
        label: t('DNIPaciente')
    }, {
        key: 'patient_full_name',
        label: t('Nombre')
    }, ...(extractDownloadHeaders(healthSurveysDownload)), {
        key: 'imc',
        label: t('IMC')
    }, {
        key: 'obesity_risk',
        label: t('Riesgo de obesidad')
    }], [healthSurveysDownload]);

    const downloadController = {
        data: healthSurveysDownload,
        downloading: healthSurveysDownloading,
        fileName: t('Cuestionarios de salud'),
        headers: downloadHeaders,
        onDownload: filters => dispatch(statisticsActions.getStatisticsHealthSurveysDownload(filters))
    };

    const onFiltersChange = filters => {
        setCurrentFilters(filters);
        dispatch(statisticsActions.getStatisticsHealthSurveys(clearObject({
            ...filters
        })));
    };

    const {
        chronic_disease,
        chronic_disease_distribution,
        general_health_status,
        harmful_habits_drinking,
        harmful_habits_smoke,
        imc,
        imc_distribution,
        last_medical_checkup,
        men_over_40,
        men_over_40_total,
        reason_no_medical_visit,
        stress,
        total,
        unsatisfactory_social_life,
        women_over_35,
        women_over_35_total
    } = healthSurveys;

    const parseChart = useCallback((data, label) => ({
        config: [{ dataKey: 'value', fontSize: '11px', label }],
        data: objectToChart(data, parseKey),
        height: 300
    }));

    const chronicDisease = useMemo(() => parseChart(chronic_disease_distribution, t('Enfermedades crónicas')), [chronic_disease_distribution]);
    const genderFemale = useMemo(() => parseChart(women_over_35, t('Mujeres de más de 35 años')), [women_over_35]);
    const genderMale = useMemo(() => parseChart(men_over_40, t('Hombres mayores a 40 años')), [men_over_40]);
    const generalHealthStatus = useMemo(() => parseChart(general_health_status, t('Autopercepción de salud')), [general_health_status]);
    const imcDistribution = useMemo(() => parseChart(imc_distribution, t('Distribución según IMC')), [imc_distribution]);
    const harmfulHabitsDrinking = useMemo(() => parseChart(harmful_habits_drinking, t('Consumo de alcohol')), [harmful_habits_drinking]);
    const harmfulHabitsSmoke = useMemo(() => parseChart(harmful_habits_smoke, t('Fumadores')), [harmful_habits_smoke]);
    const lastMedicalCheckup = useMemo(() => parseChart(last_medical_checkup, t('Último control médico general')), [last_medical_checkup]);
    const reasonNoMedicalVisit = useMemo(() => parseChart(reason_no_medical_visit, t('Motivos de no realizarse el control anual')), [reason_no_medical_visit]);
    const workStress = useMemo(() => parseChart(stress, t('Estrés laboral')), [stress]);

    return (
        <StatisticsLayout
            downloadController={ !isLocked(user, 'download_reports') ? downloadController : undefined }
            filterProfessionals={ false }
            filterSpecialties={ false }
            loading={ loading }
            tabController={ tabController }
            onFiltersChange={ onFiltersChange }
        >
            <Row>
                <Col md="6" xs="12">
                    <InfoCard
                        caption={ t('Cantidad de cuestionarios') }
                        icon="summarize"
                        title={ (total ?? 0).toString() }
                    />
                </Col>
                {
                    !!healthSurveys?.total &&
                    <>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ t('Autopercepción de salud') }>
                                <PieChart { ...generalHealthStatus } />
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ t('Último control médico general') }>
                                <PieChart { ...lastMedicalCheckup } />
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ t('Motivos de no realizarse el control anual') }>
                                <PieChart { ...reasonNoMedicalVisit } />
                            </Card>
                        </Col>
                        <Col md="6" xs="12">
                            <InfoCard
                                caption={ t('Trabajadores con posible obesidad según IMC') }
                                icon="scale"
                                title={ `${ imc?.total ?? 0 } (${ imc?.percentage ?? 0 } %)` }
                            />
                        </Col>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ `${ t('Distribución según IMC') }` }>
                                <PieChart { ...imcDistribution } />
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ `${ t('Mujeres de más de 35 años') } (${ women_over_35_total })` }>
                                <BarChart { ...genderFemale } />
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ `${ t('Hombres mayores a 40 años') } (${ men_over_40_total })` }>
                                <BarChart { ...genderMale } />
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ `${ t('Fumadores') }` }>
                                <PieChart { ...harmfulHabitsSmoke } />
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ `${ t('Consumo de alcohol') }` }>
                                <PieChart { ...harmfulHabitsDrinking } />
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <Card overflowX="auto" title={ t('Estrés laboral') }>
                                <PieChart { ...workStress } />
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <InfoCard
                                caption={ t('Vida social insatisfactoria') }
                                icon="percent"
                                title={ (unsatisfactory_social_life ?? 0).toString() }
                            />
                        </Col>
                        <Col md="6" xs="12">
                            <InfoCard
                                caption={ t('Trabajadores con enfermedades crónicas diagnosticadas') }
                                icon="scale"
                                title={ `${ chronic_disease?.total ?? 0 } (${ chronic_disease?.percentage ?? 0 } %)` }
                            />
                        </Col>
                        <Col sm="6" xs="12">
                            {
                                !isEmpty(chronic_disease_distribution) &&
                                <Card overflowX="auto" title={ t('Enfermedades crónicas') }>
                                    <PieChart { ...chronicDisease } />
                                </Card>
                            }
                        </Col>
                    </>
                }
            </Row>
        </StatisticsLayout>
    );
};

HealthSurveys.propTypes = {
    tabController: PropTypes.object
};

export default HealthSurveys;
